import type { FormDefinition, FormField, FormTemplateView, FormValidity } from "@vaulted/shared";
import { CHART_TEMPLATES, type ChartTemplateKey, INTAKE_FORM_KEY, validityLabel } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, get, post, put } from "../../api";
import { Alert, Button, Checkbox, Field, Loading, Panel, PanelHeader, SelectField, TextAreaField, formatDateTime } from "../../components/ui";
import { SettingsForm, type SettingsResponse, useDraft } from "./common";

/** Client forms' wording. Each save makes a new version; signed copies keep the wording they were signed with. */
export function FormsTab({ data }: { data: SettingsResponse }) {
  const list = useQuery({ queryKey: ["form-templates"], queryFn: () => get<FormTemplateView[]>("/api/form-templates") });
  const [openId, setOpenId] = useState<string | null>(null);
  if (list.isLoading) return <Loading />;
  if (list.error || !list.data) return <Alert>{errorMessage(list.error)}</Alert>;
  const open = list.data.find((t) => t.id === openId);
  if (open) return <FormEditor key={open.versionId} t={open} onClose={() => setOpenId(null)} />;
  return (
    <div className="flex flex-col gap-6">
      <ChartsPanel data={data} />
    <Panel>
      <PanelHeader title="Client forms" description="What clients read and sign before their visit. Services choose which forms they need (Catalog → item → Charting and forms)." />
      <ul className="px-5 pb-3">
        {list.data.map((t) => (
          <li key={t.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-mist py-3 last:border-0">
            <span className="min-w-0 flex-1">
              <span className="font-semibold">{t.name}</span>
              <span className="block text-sm text-muted">
                {validityLabel(t.validity)}
                {t.key !== INTAKE_FORM_KEY && !t.sendWithIntake && " · skipped when the intake is sent"} · version {t.version}, {formatDateTime(t.updatedAt)}
              </span>
            </span>
            <Button variant="quiet" onClick={() => setOpenId(t.id)}>
              Edit wording
            </Button>
          </li>
        ))}
      </ul>
    </Panel>
    </div>
  );
}

/** Which kinds of chart the medical director must review after the nurse signs. */
function ChartsPanel({ data }: { data: SettingsResponse }) {
  const d = useDraft(data.settings, ["charts.mustReview"] as const, "Chart review saved.");
  const list = d.value["charts.mustReview"];
  return (
    <Panel>
      <PanelHeader title="Charts" description="Charts of these kinds go to Pending Charts for the medical director to review once the nurse signs." />
      <SettingsForm onSave={d.save} saving={d.saving} error={d.error} notice={d.notice} dirty={d.dirty}>
        {(Object.keys(CHART_TEMPLATES) as ChartTemplateKey[]).map((k) => (
          <Checkbox
            key={k}
            label={`${CHART_TEMPLATES[k].name}: must be reviewed`}
            checked={list.includes(k)}
            onChange={(on) => d.set("charts.mustReview", on ? [...list, k] : list.filter((x) => x !== k))}
          />
        ))}
      </SettingsForm>
    </Panel>
  );
}

function FormEditor({ t, onClose }: { t: FormTemplateView; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(t.name);
  const [validity, setValidity] = useState<FormValidity>(t.validity);
  const [def, setDef] = useState<FormDefinition>(t.definition);
  const [requireResign, setRequireResign] = useState(false);
  const [sendWithIntake, setSendWithIntake] = useState(t.sendWithIntake);
  const save = useMutation({
    mutationFn: () => put<FormTemplateView>(`/api/form-templates/${t.id}`, { name, validity, definition: def, requireResign, sendWithIntake }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["form-templates"] });
      onClose();
    },
  });
  const reset = useMutation({
    mutationFn: () => post<FormTemplateView>(`/api/form-templates/${t.id}/reset`, { requireResign: false }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["form-templates"] });
      onClose();
    },
  });
  const setField = (si: number, fi: number, f: FormField) =>
    setDef({ ...def, sections: def.sections.map((s, i) => (i === si ? { ...s, fields: s.fields.map((x, j) => (j === fi ? f : x)) } : s)) });
  const lines = (s: string) => s.split("\n").map((x) => x.trim()).filter(Boolean);

  return (
    <div className="flex flex-col gap-4">
      <Panel className="p-5">
        <div className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Form name (staff see this)" value={name} onChange={(e) => setName(e.target.value)} />
            <div className="flex gap-2">
              <SelectField
                label="Clients sign it"
                className="flex-1"
                value={validity.kind}
                onChange={(e) => setValidity(e.target.value === "months" ? { kind: "months", months: 12 } : { kind: e.target.value as "every_visit" | "once" })}
              >
                <option value="every_visit">Every visit</option>
                <option value="months">Every so many months</option>
                <option value="once">Once</option>
              </SelectField>
              {validity.kind === "months" && <Field label="Months" type="number" min={1} max={60} className="w-24" value={validity.months} onChange={(e) => setValidity({ kind: "months", months: Math.max(1, Number(e.target.value) || 1) })} />}
            </div>
          </div>
          {t.key !== INTAKE_FORM_KEY && (
            <Checkbox
              label="Send with intake form?"
              description="Off: on a visit where the client fills out the intake form, this one is skipped."
              checked={sendWithIntake}
              onChange={setSendWithIntake}
            />
          )}
          <Field label="Title" value={def.title} onChange={(e) => setDef({ ...def, title: e.target.value })} />
          <Field label="Subtitle" value={def.subtitle ?? ""} onChange={(e) => setDef({ ...def, subtitle: e.target.value })} />
          <p className="text-sm text-muted">Write {"{clinic}"} where the clinic's name should appear.</p>
        </div>
      </Panel>
      {def.sections.map((s, si) => (
        <Panel key={si} className="p-5">
          <div className="flex flex-col gap-4">
            <Field
              label={s.only === "minor" ? "Section title (minors only)" : "Section title"}
              value={s.title}
              onChange={(e) => setDef({ ...def, sections: def.sections.map((x, i) => (i === si ? { ...x, title: e.target.value } : x)) })}
            />
            {s.fields.map((f, fi) => {
              switch (f.kind) {
                case "text":
                  return <TextAreaField key={fi} label="Paragraph" rows={4} value={f.text} onChange={(e) => setField(si, fi, { ...f, text: e.target.value })} />;
                case "bullets":
                  return (
                    <div key={fi} className="grid gap-2">
                      <Field label="List heading" value={f.title ?? ""} onChange={(e) => setField(si, fi, { ...f, title: e.target.value })} />
                      <TextAreaField label="Points (one per line)" rows={Math.min(10, f.items.length + 1)} defaultValue={f.items.join("\n")} onBlur={(e) => setField(si, fi, { ...f, items: lines(e.target.value) })} />
                    </div>
                  );
                case "input":
                  return <Field key={fi} label="Question" value={f.label} onChange={(e) => setField(si, fi, { ...f, label: e.target.value })} />;
                case "agree":
                  return <TextAreaField key={fi} label="Checkbox the signer must tick" rows={2} value={f.label} onChange={(e) => setField(si, fi, { ...f, label: e.target.value })} />;
                case "checklist":
                case "choice":
                  return (
                    <div key={fi} className="grid gap-2">
                      <Field label={f.kind === "choice" ? "Question (pick one)" : "Checklist question"} value={f.label} onChange={(e) => setField(si, fi, { ...f, label: e.target.value })} />
                      {f.qualiphy && <p className="text-xs text-muted">Answers go to Qualiphy's screening question #{f.qualiphy.questionId}. Keep the choices' wording as it is, or that choice won't be sent.</p>}
                      <TextAreaField
                        label="Choices (one per line)"
                        hint="Answers are saved by their wording, so rewording a choice starts it fresh."
                        rows={Math.min(14, f.options.length + 1)}
                        defaultValue={f.options.join("\n")}
                        onBlur={(e) => {
                          const options = lines(e.target.value);
                          setField(si, fi, { ...f, options, flag: (f.flag ?? []).filter((x) => options.includes(x)) });
                        }}
                      />
                      <fieldset>
                        <legend className="text-sm font-semibold text-ink-soft">Shown in red to the nurse</legend>
                        <div className="grid sm:grid-cols-2">
                          {f.options.map((o) => (
                            <Checkbox key={o} label={o} checked={!!f.flag?.includes(o)} onChange={(on) => setField(si, fi, { ...f, flag: on ? [...(f.flag ?? []), o] : (f.flag ?? []).filter((x) => x !== o) })} />
                          ))}
                        </div>
                      </fieldset>
                    </div>
                  );
                case "client_details":
                  return (
                    <p key={fi} className="text-sm text-muted">
                      Client details (name, date of birth, phone, email, address, emergency contact), filled in from their record.
                    </p>
                  );
                case "guardian_details":
                  return (
                    <p key={fi} className="text-sm text-muted">
                      Parent or guardian's name, relationship, and phone.
                    </p>
                  );
              }
            })}
          </div>
        </Panel>
      ))}
      <Panel className="p-5">
        <Checkbox label="Everyone signs this again" description="Clients who signed an earlier version are asked to sign this one at their next visit. Leave off for small wording fixes." checked={requireResign} onChange={setRequireResign} />
      </Panel>
      {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      <div className="flex gap-2">
        <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
          Save new version
        </Button>
        <Button variant="quiet" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="quiet"
          className="ml-auto"
          busy={reset.isPending}
          onClick={() => window.confirm("Replace this form's wording with the built-in wording (a new version)? Signed copies keep theirs.") && reset.mutate()}
        >
          Use the built-in wording
        </Button>
      </div>
      {reset.error && <Alert>{errorMessage(reset.error)}</Alert>}
    </div>
  );
}
